/**
 * Top-level Map view.
 *
 * Stream A PR 2 — commits M1 (canvas + tile-source), M2 (snapshot
 * rendering), M3 (SSE live-updates), M4 (low-zoom clustering), and
 * M5 (trail polyline for moving Producers + minimal click-to-
 * select) compose here. M6 expands the selection bus + adds the
 * simple Entity Details panel.
 *
 * # Composition
 *
 * - A single ``<div data-testid="map-canvas">`` provides the
 *   container element. ``useMap`` constructs the :class:`Map`
 *   against it on mount and tears down on unmount.
 * - The style is built once per component lifetime via
 *   :func:`buildMapStyleFromSpec` (from the resolved tile descriptor)
 *   and memoised so the hook's hot-swap effect doesn't fire on every
 *   render.
 * - Initial viewport defaults to the global view (see
 *   :const:`DEFAULT_INITIAL_VIEWPORT`, which mirrors the built-in
 *   default Profile home so the first paint never flashes a regional
 *   framing); a Profile Apply re-targets nav-home per REQ-UI-090.
 * - On the map's ``load`` event, the loaded :class:`Map` is
 *   captured into component state so the :class:`SnapshotLayer`
 *   can mount after the map is ready — MapLibre rejects
 *   ``addSource`` before ``load``.
 * - The initial viewport bounds + ``snapshotSince`` are computed
 *   on first load; subsequent pan/zoom updates flow through
 *   :func:`installViewportSync` (debounced ``moveend`` listener)
 *   so the snapshot + SSE re-fire against the new bbox. Closes
 *   REQ-UI-007's "pan-driven re-subscribe" follow-up.
 *
 * # Tile routing (REQ-UI-033 / REQ-UI-034; ADR-016 amended 2026-06-10)
 *
 * The basemap source is **config-driven**: :func:`useTileDescriptor`
 * resolves the advertised ``default: true`` :type:`TileSourceDescriptor`
 * from ``GET /api/config``. While that is pending / errored the map
 * renders **no** basemap (theme background only; markers still paint)
 * and the basemap is added when the descriptor lands — never a network
 * guess: a guessed host the deployment's CSP forbids never loads, so
 * MapLibre never fires ``load`` and nothing could recover (#1002;
 * CLAUDE.md §8.1 — fail-safe is "no basemap, briefly"). The
 * descriptor's ``url_template`` drives the source:
 * an **absolute** template (e.g. NRL Blue Marble) is fetched
 * **browser-direct** (REQ-UI-033 — the operator's browser reaches a
 * per-network tile server the air-gapped backend cannot); a **relative**
 * ``/api/tiles/...`` template rides the same-origin BFF tile-proxy
 * (REQ-UI-034 — Vite dev proxy → BFF :8080 in dev, deployed nginx → BFF
 * in production). The CSP that authorises an absolute browser-direct
 * host is a *later* increment (REQ-UI-035 — BFF/nginx, not the UI).
 *
 * # Accessibility
 *
 * Flow 02 §"Accessibility notes" mandates
 * ``role="application"`` + keyboard pan/zoom. The container
 * carries the role + ARIA label; the marker-tab cycling (M3)
 * attaches once markers exist. The map canvas itself is a
 * ``<canvas>`` element MapLibre injects; that canvas inherits the
 * container's role per ARIA composition.
 *
 * # Theme
 *
 * The map honors the operator's theme preference (REQ-UI-040 /
 * REQ-UI-041 / REQ-UI-045). The active *choice* (light / dark /
 * system) is read from the :class:`PreferencesContext`;
 * :func:`useResolvedTheme` collapses ``system`` to light/dark via
 * ``prefers-color-scheme`` and re-renders on OS-theme flips. The
 * **initial** style is built for the resolved theme so first paint
 * is correct in dark. Subsequent flips are applied by
 * :func:`applyMapTheme` as a targeted source/paint update — NOT a
 * ``setStyle`` swap, which would wipe the imperatively-added data
 * layers (see :file:`use-map.ts` docstring). The dark-basemap
 * behaviour rides the descriptor (REQ-UI-045): a descriptor with a
 * ``dark_layer`` points the source at a real dark basemap on dark
 * theme; a ``null`` ``dark_layer`` (NRL daytime imagery) keeps the
 * in-shader raster darkening fallback.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre).
 *   - REQ-UI-003 (snapshot on map open).
 *   - REQ-UI-004 (snapshot render ≤ 500 ms).
 *   - REQ-UI-006 (live update marker patch via SSE).
 *   - REQ-UI-012 (trail rendering — M5).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-UI-033 / REQ-UI-034 (config-driven browser-direct tiles +
 *     retained BFF proxy fallback).
 *   - REQ-UI-035 (BFF-only — the absolute-host CSP is a later increment).
 *   - REQ-UI-040 / REQ-UI-041 / REQ-UI-045 (theme + theme-aware tiles).
 *   - REQ-UI-091 (Profile-selected basemap — later consumer).
 *   - ADR-016 §3 (snapshot orchestration) + §4 (WebGL) + §5 (trail)
 *     + 2026-06-10 amendment (config-driven browser-direct tiles).
 *   - ADR-024 §3 (selection bus — M5 minimal seam; PR 5 expansion).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 *   - docs/engineering/detail/storyboards/flows/04-entity-detail/README.md
 *     §"Selection model" (M5 wires the marker-click write path;
 *     deselection is M6).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Map as MapLibreMap, MapMouseEvent } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// Override MapLibre's default grab/grabbing "hand" cursor on the map
// surface with the default arrow cursor. MUST be imported after the
// MapLibre stylesheet so the equal-specificity rules win on source order.
import "./map-cursor.css";
// Re-skin MapLibre's scale control to the Meridian light/dark theme.
// Same post-maplibre-gl.css import-order discipline as map-cursor.css.
import "./map-controls.css";
// Re-skin the annotation measurement hover tooltip (#8); same import-order.
import "./annotation-tooltip.css";
import { buildMapStyleFromSpec } from "@/map/style";
import { runWhenStyleReady } from "@/map/map-ready";
import {
  applyMapTheme,
  basemapRebuildNeeded,
  liveBasemapKind,
  liveVectorSourceUrl,
  rebuildBasemap,
} from "@/map/use-map-theme";
import { useTileDescriptor } from "@/map/use-tile-descriptor";
import {
  useMap,
  DEFAULT_INITIAL_VIEWPORT,
  distanceUnitToScaleUnit,
  profileHomeToViewport,
  type MapInitialViewport,
} from "@/map/use-map";
import { useResolvedTheme } from "@/theme/resolve-theme";
import { usePreferences } from "@/preferences/preferences-context";
import type { Theme } from "@/theme/tokens";
import { useSnapshot } from "@/map/use-snapshot";
import { useTracksFeed } from "@/map/use-tracks-feed";
import { useObjectTracksFeed } from "@/map/use-object-tracks-feed";
import { SnapshotLayer } from "@/map/snapshot-layer";
import { SNAPSHOT_LAYER_ID } from "@/map/snapshot-layer-impl";
import { registerGlyphIcons } from "@/map/glyphs";
import {
  stableOverrideKey,
  type PlatformColorConfig,
  type AlertColorConfig,
} from "@/map/marker-styling";
import { isBackgroundClick } from "@/map/background-deselect";
import { ClusterLayer } from "@/map/cluster-layer";
import { TrailLayer } from "@/map/trail-layer";
import { CoverageLayer } from "@/map/coverage-layer";
import { ObscurationLayer } from "@/map/obscuration-layer";
import { FovLayer } from "@/map/fov-layer";
import { DetectionLayer } from "@/map/detection-layer";
import { AlertLayer } from "@/map/alert-layer";
import { CursorReadout } from "@/map/cursor-readout";
import { TrackTailLayer } from "@/map/track-tail-layer";
import { ObjectTrackLayer } from "@/map/object-track-layer";
import type { SnapshotViewport } from "@/map/snapshot-types";
import { buildPlatformPositionIndex } from "@/map/snapshot-types";
import { useSetMapViewport, useSseConnection } from "@/map/sse-context";
import { useMapSseBridges } from "@/map/use-map-sse-bridges";
import { useSelectionStore } from "@/selection/selection-store";
import { usePlatformFocusFlyTo } from "@/map/use-platform-focus-fly-to";
import { useAnnotationDraw } from "@/map/use-annotation-draw";
import { useAnnotationDrag } from "@/map/use-annotation-drag";
import { useAnnotationMeasurementHover } from "@/map/use-annotation-measurement-hover";
import { AnnotationPalette } from "@/annotations/annotation-palette";
import { AnnotationLabelLayer } from "@/map/annotation-label-layer";
import { AnnotationRenderLayer } from "@/map/annotation-render-layer";
import { AnnotationHandleLayer } from "@/map/annotation-handle-layer";
import { AnnotationContextMenu } from "@/map/annotation-context-menu";
import { installViewportSync, viewportFromBounds, viewportsEqual } from "@/map/viewport-sync";
import { useMapViewportStore } from "@/map/map-viewport-store";
import { publishMapViewport } from "@/map/publish-map-viewport";
import { useNotify } from "@/notifications/use-notify";
import type { MapDegradedInfo } from "@/map/basemap-readiness";

/**
 * Legacy BFF tile-proxy base URL — the same-origin relative fallback
 * (REQ-UI-034). Superseded as the *authoritative* basemap source by
 * the config-driven descriptor (``useTileDescriptor`` →
 * :type:`TileSourceSpec`; ADR-016 amended 2026-06-10): the style
 * factory ignores this ``tileBaseUrl`` slot whenever a ``spec`` is
 * supplied (which it always is here). Retained as the harmless value
 * for that ignored positional/option so the factory's legacy signature
 * stays intact, and as the documented relative-proxy base a relative
 * descriptor ``url_template`` resolves against.
 *
 * Cross-references:
 *   - REQ-UI-034 (retained BFF proxy) / REQ-UI-035 (same-origin).
 *   - ADR-016 amended 2026-05-12 + 2026-06-10.
 */
const TILE_PROXY_BASE_URL = "/api/tiles";

/**
 * Operator-facing headline when the basemap fails to load (REQ-UI-166).
 *
 * The wording is doing real work. A map with no basemap is not obviously
 * broken — the operator sees the theme background with markers floating on
 * it, which reads exactly like "there is no terrain data for this area" or,
 * worse, like a normal display. The message therefore has to say two things:
 * the basemap is *missing*, and the tracks that remain are *still good*.
 * Silence here would be an operator drawing conclusions from a display whose
 * degradation nobody told them about (CLAUDE.md §8.1).
 */
export const BASEMAP_UNAVAILABLE_MESSAGE =
  "Basemap unavailable — platform positions are still live";

/**
 * Collapses repeat reports onto one live notification. The raster basemap
 * path emits one MapLibre error per failed tile (25 for a single blocked
 * host in the live probe), and a remount re-reports; the operator gets one
 * banner either way.
 */
export const BASEMAP_UNAVAILABLE_DEDUPE_KEY = "map:basemap-unavailable";

/**
 * Render the Meridian Map view.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS + WebGL marker rendering).
 *   - REQ-UI-003 / REQ-UI-004 (snapshot on map open).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 *   - ADR-016 §3 / §4.
 */
export function Map(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Push the map's bbox into the SSE provider so the EventSource
  // subscribes against the right viewport (REQ-UI-007). The setter
  // is stable across renders per React's :func:`useState`
  // guarantee, so it's safe to use as an effect dependency.
  const setSseViewport = useSetMapViewport();

  // Subscribe to the SSE connection so the :class:`SnapshotLayer`
  // can apply ``status`` deltas as they arrive (REQ-UI-006). The
  // hook returns the current state machine state + the stream's
  // ``subscribe``; each consumer registers its own handler.
  const sse = useSseConnection();

  // Live-SSE → shared-store bridges (ADR-032 PR6 re-home). Keep the
  // detections / alerts / status-geometry stores warm off the SAME single
  // ``sse.subscribe`` feeder so the Detections + Alerts panels, the
  // DetectionLayer / AlertLayer glyphs, the Flow-F notifications, and the
  // FoV / coverage / obscuration washes stay live. Previously mounted inside
  // the now-removed EventLayer; re-homed here so Event removal doesn't drop
  // them (the read-only layers below mount NO bridge of their own — they
  // only read these stores). The dynamic-track bridge stays in
  // TrackTailLayer, which owns it.
  useMapSseBridges(sse.subscribe);

  // Selection bus — two-slot Zustand store (REQ-UI-027 / REQ-UI-051 /
  // ADR-024 §5; 2026-06-09 selection-sync redesign). A marker click is a
  // map-originated selection, so the handler below calls ``selectOnMap``
  // to set BOTH the ``inspected`` slot (Entity Details) and the
  // ``mapSelected`` slot (ring + camera + trail). The :class:`TrailLayer`
  // reads ``mapSelected`` via :func:`useMapSelectedProducerId`.
  //
  // We read the actions via narrow selectors so the component doesn't
  // re-render when the selection value changes — only the click handlers
  // need the actions, not the visible selection state.
  //
  // Cross-references:
  //   - REQ-UI-012 (trail rendering reads the map slot of the bus).
  //   - REQ-UI-051 (synchronized selection contract).
  //   - ADR-024 §5.
  const selectOnMap = useSelectionStore((s) => s.selectOnMap);
  const clear = useSelectionStore((s) => s.clear);

  // Operator theme preference (REQ-UI-040). The *choice* (one of
  // light / dark / system) lives in the working state's ``theme``
  // slice (ADR-033 §3.1); :func:`useResolvedTheme` collapses
  // ``system`` to light/dark via ``prefers-color-scheme`` and
  // re-renders this component when the OS theme flips while
  // ``system`` is selected (REQ-UI-041). The resolved value drives
  // both the initial style build and the runtime
  // :func:`applyMapTheme` flip.
  const { workingState } = usePreferences();
  const themeChoice = (workingState.theme as Theme | undefined) ?? "system";
  const resolvedTheme = useResolvedTheme(themeChoice);

  // Config-driven basemap tile source (REQ-UI-033 / REQ-UI-034; ADR-016
  // amended 2026-06-10). Resolves the advertised ``default: true``
  // descriptor from ``GET /api/config`` (via :func:`useDeploymentConfig`).
  // ``null`` while the config is pending / errored ⇒ the map renders NO
  // basemap until it resolves (#1002 — a network guess the CSP may block
  // would wedge MapLibre's ``load`` for good). ``spec`` is the derived
  // :type:`TileSourceSpec` the style factory + the theme flip consume; an
  // absolute ``url_template`` is fetched browser-direct, a relative
  // ``/api/tiles/...`` one rides the retained BFF proxy. Profile-based
  // selection (REQ-UI-091) is applied inside the seam.
  const resolvedTile = useTileDescriptor();
  const tileSpec = resolvedTile?.spec ?? null;
  // Hold the latest spec in a ref so the once-at-mount initial-style memo
  // reads it without listing it in its (empty) deps — the canvas is built
  // once (with no basemap when ``null``); a spec that resolves / changes
  // after mount is applied by the effect below (which DOES depend on
  // ``tileSpec``) as a basemap sub-graph build / flip — never by
  // rebuilding the style (a rebuild would wipe the data layers).
  const initialTileSpecRef = useRef(tileSpec);

  // Nav-home ← the active Profile's ``map.home`` (REQ-UI-090; ADR-033 §9.4).
  // ``profileHomeToViewport`` converts the ``{ center: {lon,lat}, zoom }``
  // Profile shape to MapLibre's ``[lon, lat]`` tuple (or ``null`` when the
  // Profile carries no usable home). The reset-view control reads this LIVE
  // (via the ``getHome`` getter below) so a Profile Apply re-targets
  // nav-home; the camera also flies here on Apply / first load (the effect
  // further down). The fallback is the static CONUS viewport.
  const homeViewport = useMemo<MapInitialViewport | null>(
    () => profileHomeToViewport(workingState.map?.home),
    [workingState.map?.home],
  );
  // Keep the latest home in a ref so the ``getHome`` getter passed to
  // ``useMap`` (constructed once at mount) always reads the current value
  // without re-mounting the map. The getter resolves the fallback so the
  // reset control never flies to ``null``.
  const homeViewportRef = useRef(homeViewport);
  homeViewportRef.current = homeViewport;
  const getHome = useCallback((): MapInitialViewport => {
    return homeViewportRef.current ?? DEFAULT_INITIAL_VIEWPORT;
  }, []);

  // Scale-bar unit ← the active Profile's ``units.distance`` (REQ-UI-083;
  // ADR-033 §7), mapped to MapLibre's built-in scale unit. Drives the
  // INITIAL control unit (passed to ``useMap``) and live changes (the effect
  // further down calls ``handle.setScaleUnit``). NOTE: this is MapLibre's
  // built-in scale rendering — the increment-5 ``formatDistance`` primitive
  // is not its backing (that lands at a future detail-panel range field).
  const distanceUnit = workingState.units?.distance;
  const scaleUnit = distanceUnitToScaleUnit(distanceUnit);

  // Marker COLOUR config ← the active Profile's ``map.{platform,detection,alert}``
  // slices (inc6b; REQ-UI-086/087/088; ADR-033 §9.2/§9.7). COLOUR ONLY — passed
  // into the layer components below, which re-apply the paint in place on a
  // Profile Apply / theme flip. The configs are memoised on the primitive
  // sub-fields (not the ``map`` object identity, which is a fresh reference each
  // render) so the layers' effect deps stay stable until a colour setting
  // actually changes.
  //
  // The marker SHAPE per entity type is the active Profile's single
  // ``map.<type>.glyph`` id (GLYPHS batch; REQ-UI-086/087/088), from the closed
  // :data:`map/glyphs.GLYPH_IDS` vocabulary — threaded as a primitive string
  // into each layer (an unknown id falls back to ``circle``). The per-NodeType
  // platform map was removed with #367.
  //
  // The basemap selection (``map.tile_server``; REQ-UI-091, #366) is consumed
  // separately by :func:`useTileDescriptor` above (it drives the MapLibre tile
  // source, not these data-layer paint slices); ``map.basemap`` remains an
  // unconsumed POST-MVP hook (a single id picks the source today).
  const platformColorMode = workingState.map?.platform?.color_mode;
  const platformSingleColor = workingState.map?.platform?.single_color;
  const platformGlyph = workingState.map?.platform?.glyph;
  // Per-value override maps (``status_colors`` / ``type_colors`` /
  // ``priority_colors``; inc-items 4/5, REQ-UI-086/088) — sparse maps the
  // by-value modes honour over their design-system token. Memoised on a stable
  // serialization (``stableOverrideKey``) so the config identity — and the
  // layers' in-place repaint — change exactly when an override changes, not on
  // every render (the working-state ``map`` is a fresh object each render).
  const platformStatusColors = workingState.map?.platform?.status_colors;
  const platformStatusColorsKey = stableOverrideKey(platformStatusColors);
  const platformColorConfig = useMemo<PlatformColorConfig>(
    () => ({
      mode: platformColorMode,
      singleColor: platformSingleColor,
      statusColors: platformStatusColors,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key is the stable stand-in for the object
    [platformColorMode, platformSingleColor, platformStatusColorsKey],
  );
  const detectionSingleColor = workingState.map?.detection?.single_color;
  const detectionGlyph = workingState.map?.detection?.glyph;
  const alertColorMode = workingState.map?.alert?.color_mode;
  const alertSingleColor = workingState.map?.alert?.single_color;
  const alertGlyph = workingState.map?.alert?.glyph;
  const alertTypeColors = workingState.map?.alert?.type_colors;
  const alertPriorityColors = workingState.map?.alert?.priority_colors;
  const alertTypeColorsKey = stableOverrideKey(alertTypeColors);
  const alertPriorityColorsKey = stableOverrideKey(alertPriorityColors);
  const alertColorConfig = useMemo<AlertColorConfig>(
    () => ({
      mode: alertColorMode,
      singleColor: alertSingleColor,
      typeColors: alertTypeColors,
      priorityColors: alertPriorityColors,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keys are the stable stand-ins for the objects
    [alertColorMode, alertSingleColor, alertTypeColorsKey, alertPriorityColorsKey],
  );

  // Loaded map instance — populated by the ``onMapReady`` callback
  // after MapLibre's ``load`` event fires. Held in state (not a
  // ref) so the :class:`SnapshotLayer` re-renders when the map
  // becomes available.
  const [loadedMap, setLoadedMap] = useState<MapLibreMap | null>(null);

  // ``aria-live`` region ref. The :class:`ClusterLayer` writes a
  // one-sentence summary on viewport recompute so screen-reader
  // users get a coarse "what's on screen" cue per REQ-UI-059. The
  // ref lives on the parent so the region stays mounted across
  // :class:`ClusterLayer` remount cycles (e.g., snapshot reload).
  //
  // Cross-references:
  //   - REQ-UI-010 / REQ-UI-059.
  //   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
  //     §"Accessibility".
  const clusterSummaryRef = useRef<HTMLDivElement | null>(null);

  // Viewport bounds for the snapshot query. Captured on first map
  // load and then refreshed on every settled pan/zoom via
  // :func:`installViewportSync` (see the ``useEffect`` below). The
  // state update re-keys the :func:`useSnapshot` TanStack Query
  // (key includes the viewport floats) so a fresh snapshot fires
  // for the new bbox; the parallel ``setSseViewport`` push closes
  // the existing :class:`EventSource` and opens a new one with the
  // new viewport query params per REQ-UI-007. Closes the M4 follow-
  // up flagged in the 2026-05-27 empty-map diagnostic. Both writers
  // go through ``publishViewport`` (below), which skips a value-equal
  // update.
  const [snapshotViewport, setSnapshotViewport] = useState<SnapshotViewport | null>(null);

  // ``since`` for the snapshot query. Stable per mount — re-renders
  // must not invalidate the snapshot's cache key. ``useState`` with
  // a lazy initialiser captures ``Date.now()`` exactly once.
  const [snapshotSince] = useState(() => new Date());

  // The one gate both viewport sources (``onMapReady`` below and the settled
  // ``moveend`` path) pass through. Callers hand it a viewport already read
  // through :func:`viewportFromBounds` (clamped + quantised to the 1e-4°
  // grid), and it publishes only when the value actually changed: a camera
  // that settles on the same view — the first-load home ``flyTo`` ("a visual
  // no-op", below), an inertia settle — reads back bounds that differ only in
  // the last digits, and a new object with the same quantised bounds would
  // still re-run :func:`useSse`'s effect (its dependency is the object) and
  // close / re-open the :class:`EventSource`, and re-render every
  // viewport-keyed hook. Keyed raw, that was ~2.5 ``tracks`` requests per
  // distinct viewport (ADR-016 amendment 2026-10-01). Held in a ref rather
  // than read from ``snapshotViewport`` state so the comparison is against
  // what was last *published*, not what React has rendered yet.
  const lastPublishedViewportRef = useRef<SnapshotViewport | null>(null);
  const publishViewport = useCallback(
    (viewport: SnapshotViewport): void => {
      const previous = lastPublishedViewportRef.current;
      if (previous !== null && viewportsEqual(previous, viewport)) {
        return;
      }
      lastPublishedViewportRef.current = viewport;
      setSnapshotViewport(viewport);
      setSseViewport(viewport);
    },
    [setSseViewport],
  );

  // Build the INITIAL style once, for whatever theme resolves at
  // first mount, so the very first paint is correct in dark
  // (REQ-UI-040). The empty dependency array is intentional: the
  // canvas is constructed from this style exactly once by
  // :func:`useMap`; subsequent theme flips are applied by
  // :func:`applyMapTheme` as a targeted source/paint update (the
  // effect below), NOT by rebuilding the style — rebuilding would
  // re-mount the canvas and wipe the data layers. We therefore
  // capture the first-mount resolved theme via a ref so the memo
  // body reads a stable value without listing ``resolvedTheme`` in
  // its deps (which would defeat the once-only construction).
  const initialThemeRef = useRef(resolvedTheme);
  const style = useMemo(
    // Reads refs, not ``resolvedTheme`` / ``tileSpec`` directly, so the
    // build runs once at mount; the empty deps array is correct (refs are
    // not reactive). Theme flips + a late/changed tile descriptor are
    // applied by :func:`applyMapTheme` below. The descriptor ``spec`` is
    // authoritative for the basemap source (browser-direct ``url_template``
    // / ``maxzoom`` / attribution; ADR-016 amended 2026-06-10).
    () => buildMapStyleFromSpec(initialThemeRef.current, initialTileSpecRef.current),
    [],
  );

  // Operator-visible degradation reporting (REQ-UI-166). ``useNotify``'s
  // return is referentially stable, so this closure is safe to hand to a hook
  // that reads it live.
  const notify = useNotify();
  const handleMapDegraded = useCallback(
    (info: MapDegradedInfo) => {
      // Engineer half first — the console line carries the source id and the
      // underlying message for a deployment nobody can SSH into (§5.4).
      console.error(
        "[meridian/map] basemap unavailable — rendering data layers over the",
        "theme background (REQ-UI-166).",
        { reason: info.reason, sourceId: info.sourceId, detail: info.message },
      );
      // Operator half — the console is not a surface a deployed operator
      // reads (CLAUDE.md §8.1).
      notify({
        severity: "warning",
        title: BASEMAP_UNAVAILABLE_MESSAGE,
        body:
          "The map background could not be loaded. Platform positions, tracks and " +
          "detections are unaffected. Ask your administrator to check the basemap " +
          "tile source for this deployment.",
        source: "system",
        dedupeKey: BASEMAP_UNAVAILABLE_DEDUPE_KEY,
        // A degraded basemap persists until the deployment is fixed. An
        // auto-expiring toast would leave a permanently wrong display with
        // nothing on screen to explain it.
        sticky: true,
      });
    },
    [notify],
  );

  const handle = useMap(containerRef, {
    style,
    // First paint uses the Profile home when present so the operator opens
    // on their saved viewport (REQ-UI-090); the post-load flyTo effect below
    // re-confirms it (and handles a home that arrives after mount). Falls
    // back to the static CONUS viewport when no home is set.
    initial: homeViewport ?? DEFAULT_INITIAL_VIEWPORT,
    // Live nav-home getter — read on every reset-view click (REQ-UI-090).
    getHome,
    // Initial scale-bar unit (REQ-UI-083); live updates via the effect below.
    scaleUnit,
    onMapReady: (map) => {
      // Capture both the instance + initial bounds in one
      // callback. The bounds read here are the source of truth
      // for the FIRST snapshot + SSE subscribe. Subsequent
      // pan/zoom updates flow through the moveend useEffect
      // below (via :func:`installViewportSync`) — both paths
      // read through :func:`viewportFromBounds` and publish
      // through ``publishViewport`` so the snapshot + SSE refresh
      // alongside the visible viewport and never on a no-op.
      //
      // :func:`viewportFromBounds` clamps (per the post-#245
      // diagnostic — MapLibre's ``getBounds()`` can briefly return
      // values outside the canonical [-180, 180] / [-90, 90] range,
      // floating-point math at the projection edges, especially
      // when the container hasn't fully laid out yet; the BFF's
      // Pydantic validator rejects any out-of-range value with
      // HTTP 422) and quantises, in lock-step with the moveend
      // handler in :func:`installViewportSync`.
      publishViewport(viewportFromBounds(map.getBounds()));
      // Seed the live camera into the viewport store so the Profile editor's
      // "Set home to current view" has a value the moment the map loads
      // (REQ-UI-076 / REQ-UI-090). Subsequent pan/zoom refreshes it via the
      // moveend path below — NO second listener (CLAUDE.md §4.4).
      publishMapViewport(map);
      // Bake + register the marker glyph SDF icons once on load (GLYPHS batch;
      // REQ-UI-086/087/088) so the platform/detection/alert symbol layers have
      // their ``icon-image`` SDFs the moment they attach. Idempotent — the
      // per-layer attaches also call this defensively (mount-order agnostic).
      registerGlyphIcons(map);
      setLoadedMap(map);
    },
    // Reached when MapLibre's ``load`` never arrived and the readiness
    // watchdog promoted the map anyway (REQ-UI-166). ``onMapReady`` above
    // runs immediately afterwards, so the data layers below still mount —
    // over the theme background instead of a basemap.
    onMapDegraded: handleMapDegraded,
  });

  // Pan/zoom-driven viewport sync (REQ-UI-007). Once the map is
  // ready, install a debounced ``moveend`` listener that pushes the
  // new bounds into ``snapshotViewport`` + ``sseViewport`` state
  // whenever the user settles. The snapshot TanStack Query is keyed
  // on viewport so a fresh request fires; :func:`useSse` closes the
  // old :class:`EventSource` and opens a new one with the new bbox
  // query params. Closes the "M4 wires it alongside clustering"
  // follow-up flagged in earlier code comments — the gap surfaced
  // as the empty-map-at-login bug on the single testbed
  // (2026-05-27 diagnostic) when the synthetic-fleet fixture lived
  // outside the UI's default CONUS-Kansas viewport and the operator
  // panning west to find data didn't trigger a re-query.
  //
  // The setters from ``useState`` are stable references per React's
  // guarantee, so it's safe to include them in the deps array
  // without thrashing the listener install/teardown.
  const handleViewportChange = useCallback(
    (viewport: SnapshotViewport) => {
      // ``viewport`` arrives clamped + quantised from
      // :func:`installViewportSync`; ``publishViewport`` drops it when it
      // equals the last published value (a settle on the same camera).
      publishViewport(viewport);
      // Refresh the live camera in the viewport store on the SAME settled
      // moveend the snapshot/SSE re-subscribe rides — so the Profile editor's
      // "Set home to current view" always reflects the current camera without
      // a second moveend listener (REQ-UI-076 / REQ-UI-090; CLAUDE.md §4.4).
      // ``loadedMap`` is non-null here (the sync is installed only after the
      // map loads — see the effect below); read its camera, not the bbox
      // (``viewport`` is the bounding box, whose centre is not the camera
      // centre at non-zero zoom).
      if (loadedMap !== null) {
        publishMapViewport(loadedMap);
      }
    },
    [publishViewport, loadedMap],
  );
  useEffect(() => {
    if (loadedMap === null) {
      return undefined;
    }
    return installViewportSync(loadedMap, handleViewportChange);
  }, [loadedMap, handleViewportChange]);

  // Apply the resolved theme + the config-driven tile source to the
  // live map (REQ-UI-040 / REQ-UI-045 / REQ-UI-033 / REQ-UI-091), never a
  // ``setStyle`` swap, so the imperatively-added overlay data layers
  // survive. Keyed on ``[loadedMap, resolvedTheme, tileSpec]`` so it
  // (a) re-applies once the map finishes loading — making the basemap
  // authoritative regardless of load timing — (b) runs on every theme flip
  // (preference change or ``system`` OS flip), and (c) reacts when the
  // resolved descriptor arrives / changes after the initial style was
  // built (config is async; never silently keep stale tiles — CLAUDE.md
  // §8.1). No-ops before ``loadedMap`` is set.
  //
  // Two paths, because the basemap *kind* can change, not just its tiles:
  //
  //   - **Source-graph rebuild** (:func:`rebuildBasemap`) when the live
  //     basemap kind differs from the resolved spec's (raster ↔ vector),
  //     or a vector source's TileJSON ``url`` changed
  //     (:func:`basemapRebuildNeeded`). ``applyMapTheme``'s in-place
  //     ``setTiles`` / per-layer re-paint CANNOT turn a raster source into
  //     a vector one — so the FIRST descriptor landing on the basemap-less
  //     initial style (config is async; the first paint carries no
  //     basemap — #1002; ``liveBasemapKind`` reports ``"none"``), any
  //     later kind change, and any Profile picker switch across kinds
  //     (REQ-UI-091), are handled here.
  //     The rebuild swaps only the basemap sub-graph (below the first
  //     overlay), so overlays stay intact — the same never-``setStyle``
  //     invariant.
  //   - **In-place flip** (:func:`applyMapTheme`) otherwise — a same-kind
  //     theme change or a raster tile re-point (its ``setTiles`` fast-path
  //     honours a late/changed raster descriptor + the dark-layer
  //     dimension; REQ-UI-045).
  //
  // Deferred through :func:`runWhenStyleReady`: ``loadedMap`` is also set
  // by the REQ-UI-166 watchdog when MapLibre's ``load`` never arrives — a
  // map mounted in a hidden tab gets no animation frame, so its style JSON
  // is not even parsed yet — and in that window every style mutator throws
  // and ``getStyle()`` is ``undefined``. The work then runs on
  // ``style.load`` instead of crashing the route (the "reading 'layers'"
  // error boundary, 2026-10-07).
  useEffect(() => {
    if (loadedMap === null) {
      return undefined;
    }
    const apply = (): void => {
      if (tileSpec === null) {
        // Descriptor unresolved (config pending / errored): nothing to build
        // or re-point — keep the background theme-correct and wait (#1002).
        applyMapTheme(loadedMap, resolvedTheme, { tileBaseUrl: TILE_PROXY_BASE_URL, spec: null });
        return;
      }
      const liveKind = liveBasemapKind(loadedMap);
      if (basemapRebuildNeeded(liveKind, liveVectorSourceUrl(loadedMap), tileSpec)) {
        rebuildBasemap(loadedMap, resolvedTheme, tileSpec);
        return;
      }
      applyMapTheme(loadedMap, resolvedTheme, { tileBaseUrl: TILE_PROXY_BASE_URL, spec: tileSpec });
    };
    return runWhenStyleReady(loadedMap, apply);
  }, [loadedMap, resolvedTheme, tileSpec]);

  // Fly to the Profile home (REQ-UI-090; ADR-033 §9.4) on first map load AND
  // whenever the home changes (a Profile Apply re-targets the viewport).
  //
  // ``useMap`` returns a fresh ``handle`` object each render, so the effect
  // must depend on ``handle`` to satisfy exhaustive-deps; to keep that from
  // re-flying on every unrelated render, the actual fly is gated on the home
  // genuinely changing since the last flight (tracked in
  // ``lastFlownHomeRef``). The null→map load transition counts as a change
  // (initial ref value is a sentinel), so first-load-with-a-home flies once
  // — a visual no-op since ``initial`` already placed the camera there — and
  // a later Apply that swaps the home flies to the new viewport. No-op when
  // no home is set (operator keeps their pan; nav-home falls back to CONUS).
  // The operator pans freely between flights; only Apply / nav-home re-home.
  const lastFlownHomeRef = useRef<MapInitialViewport | null | undefined>(undefined);
  useEffect(() => {
    if (loadedMap === null || homeViewport === null) {
      return;
    }
    if (lastFlownHomeRef.current === homeViewport) {
      return;
    }
    lastFlownHomeRef.current = homeViewport;
    handle.flyTo({ center: homeViewport.center, zoom: homeViewport.zoom });
  }, [loadedMap, homeViewport, handle]);

  // "Preview home" re-fly (REQ-UI-076; ADR-033 §9.4). The Profile editor's
  // "Preview home" button bumps ``previewHomeNonce`` in the viewport store;
  // this effect flies the camera to the CURRENT draft home each time the
  // nonce increments. We can't lean on the home-change effect above for this:
  // that effect keys on ``homeViewport`` *reference*, so after the operator
  // pans away from an unchanged draft home it would no-op — "Preview home"
  // must re-fly to the same home on demand. ``homeViewport`` here is the live
  // working-state home (the editor's preview overlay already reflects the
  // draft), falling back to CONUS when no home is set so the button is never
  // a dead end. The initial nonce (0) is skipped via the ref so mounting
  // never auto-flies.
  const previewHomeNonce = useMapViewportStore((s) => s.previewHomeNonce);
  const lastPreviewNonceRef = useRef(previewHomeNonce);
  useEffect(() => {
    if (previewHomeNonce === lastPreviewNonceRef.current) {
      return;
    }
    lastPreviewNonceRef.current = previewHomeNonce;
    if (loadedMap === null) {
      return;
    }
    const target = homeViewport ?? DEFAULT_INITIAL_VIEWPORT;
    handle.flyTo({ center: target.center, zoom: target.zoom });
  }, [previewHomeNonce, loadedMap, homeViewport, handle]);

  // Drive the scale-bar unit from ``units.distance`` (REQ-UI-083; ADR-033
  // §7) live: when the operator changes the distance unit (e.g. via a future
  // settings surface or a Profile Apply), push the mapped MapLibre unit into
  // the held ScaleControl. No-op before the control exists (pre-load) and
  // idempotent, so the per-render ``handle`` identity in the deps is benign.
  useEffect(() => {
    handle.setScaleUnit(scaleUnit);
  }, [scaleUnit, handle]);

  // Reference ``handle`` so React doesn't warn about an unused
  // return. M3+ consumes it for ``flyTo`` from toasts; for M2 the
  // handle is intentionally retained as a stable seam.
  useEffect(() => {
    void handle;
  }, [handle]);

  // Marker-click selection wiring. When the operator clicks a
  // Producer marker on the snapshot layer, the click handler writes
  // ``{kind: "platform", id}`` to BOTH selection slots via
  // ``selectOnMap`` (map-originated selection). Consumers (TrailLayer +
  // snapshot ring read ``mapSelected``; Entity Details + Platforms list
  // highlight read ``inspected``) subscribe to the store and react.
  //
  // The 2026-06-09 redesign keeps the map-click path symmetric (both
  // slots) — only list-row left-clicks set ``inspected`` alone. Empty-
  // area-click deselection is wired in the effect below (Flow Q Frame 5)
  // and clears BOTH slots; Escape / close-X deselection still lands
  // alongside the Entity Details panel.
  //
  // Cross-references:
  //   - REQ-UI-012 (trail fetch is driven by the map slot of this write).
  //   - REQ-UI-051 (synchronized selection bus).
  //   - ADR-024 §5.
  //   - docs/engineering/detail/storyboards/flows/Q-side-by-side-map-platforms/README.md.
  useEffect(() => {
    if (loadedMap === null) {
      return undefined;
    }
    const onMarkerClick = (event: MapMouseEvent): void => {
      const features = loadedMap.queryRenderedFeatures(event.point, {
        layers: [SNAPSHOT_LAYER_ID],
      });
      const feature = features[0];
      if (feature === undefined) {
        return;
      }
      const producerId = feature.properties?.producer_id;
      if (typeof producerId !== "string" || producerId.length === 0) {
        return;
      }
      selectOnMap({ kind: "platform", id: producerId });
    };
    loadedMap.on("click", SNAPSHOT_LAYER_ID, onMarkerClick);
    return () => {
      loadedMap.off("click", SNAPSHOT_LAYER_ID, onMarkerClick);
    };
  }, [loadedMap, selectOnMap]);

  // Empty-area-click deselect (storyboard Flow Q §"Frame 5"; REQ-UI-051;
  // ADR-024 §5). A map-wide click that lands on none of the entity layers
  // (platform markers + labels, clusters) clears the selection bus, so the
  // Map ring, the Platforms row highlight, and Entity Details all reset
  // together. ``isBackgroundClick`` decides via
  // queryRenderedFeatures, so this coexists with the layer-scoped select
  // handlers regardless of handler order — a click on an entity layer is
  // handled there and ignored here. ``clear`` is a stable Zustand action, so
  // the listener is registered once per map.
  useEffect(() => {
    if (loadedMap === null) {
      return undefined;
    }
    const onBackgroundClick = (event: MapMouseEvent): void => {
      if (isBackgroundClick(loadedMap, event.point)) {
        clear();
      }
    };
    loadedMap.on("click", onBackgroundClick);
    return () => {
      loadedMap.off("click", onBackgroundClick);
    };
  }, [loadedMap, clear]);

  // Platform "Select on Map" force-recenter (REQ-UI-096; the 2026-06-09
  // two-slot redesign, Phase 2; ADR-024 §5). The Platforms-panel row menu
  // raises the map-focus signal; this hook fetches the platform's position
  // from the producer-scoped ``/latest-status`` endpoint (off-viewport-safe,
  // unlike the snapshot's viewport-scoped geometry) and eases the camera to
  // it. Takes ``loadedMap`` (``null`` before load — a no-op until then); the
  // detection / alert force-recenters live in their own layers (their stores
  // already carry off-viewport coordinates). The ring is set by the snapshot
  // layer's highlight effect once the recentered platform renders.
  usePlatformFocusFlyTo(loadedMap);

  // Render the operator's persisted annotations on the map (ADR-034 Inc 2b;
  // feature-spec §5.2). Render-only — mounts Terra Draw + pushes the fetched
  // annotations in; a no-op on a null map (pre-load). Drawing tools land later.
  useAnnotationDraw(loadedMap);
  useAnnotationDrag(loadedMap);
  useAnnotationMeasurementHover(loadedMap);

  // Fire the snapshot query. ``viewport`` is ``null`` until the
  // map's ``load`` event fires, which keeps the query disabled
  // (per :func:`useSnapshot`'s ``enabled`` clause).
  const snapshotQuery = useSnapshot({
    viewport: snapshotViewport,
    since: snapshotSince,
  });

  // Per-platform position index (``producer_id`` → ``[lng, lat]``) derived from
  // the live snapshot, for anchoring unprojectable range/bearing detections at
  // their reporting platform's marker (REQ-UI-071). Viewport-scoped like the
  // snapshot, so it only anchors detections whose platform is on-screen — which
  // is exactly when the anchor is visible. Recomputed only when the snapshot
  // data changes (not on every render / pan tick).
  const platformPositions = useMemo(
    () => buildPlatformPositionIndex(snapshotQuery.data ?? null),
    [snapshotQuery.data],
  );

  // Backfill the per-sensor dynamic-track tails for the current viewport
  // (issue #278 follow-up, PR2). Shares ``snapshotViewport`` with the
  // snapshot query, so a pan/zoom re-keys both and a fresh backfill fires
  // for the new bbox. The hook seeds the shared track store as a
  // side-effect — :class:`TrackTailLayer` reads from that store — so the
  // return value is intentionally unused here. ``viewport`` is ``null``
  // until first map load, keeping the query disabled until then.
  //
  // Cross-references:
  //   - issue #278 follow-up (dynamic track tails, PR2).
  //   - ADR-016 §6.
  useTracksFeed({ viewport: snapshotViewport });

  // Backfill the live object tracks intersecting the viewport (ADR-041 D-4;
  // REQ-UI-167 / REQ-QUERY-051) — summaries + members into the shared
  // object-track store, which :class:`ObjectTrackLayer` (the lines) and the
  // Tracks panel read; the detections SSE bridge then grows them live. Same
  // viewport keying as the two feeds above.
  useObjectTracksFeed({ viewport: snapshotViewport });

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label="Meridian map view"
      data-testid="map-canvas"
      // Fill the canvas slot. ``AppShell``'s ``<main>`` gives us
      // ``flex-1 overflow-auto``; we override to ``overflow-hidden``
      // because MapLibre owns its own scrolling. ``relative`` anchors the
      // absolute-positioned CursorReadout overlay (lower-right).
      className="relative h-full w-full overflow-hidden"
    >
      {loadedMap !== null && (
        <>
          <SnapshotLayer
            map={loadedMap}
            snapshot={snapshotQuery.data ?? null}
            subscribe={sse.subscribe}
            platformColor={platformColorConfig}
            platformGlyph={platformGlyph}
          />
          <ClusterLayer
            map={loadedMap}
            snapshot={snapshotQuery.data ?? null}
            liveRegionRef={clusterSummaryRef}
            platformColor={platformColorConfig}
          />
          {/* TrailLayer — Stream A PR 2 / commit M5. Reads selection
           * from :class:`SelectionContext`; fetches trail via
           * :func:`useTrail`; attaches MapLibre line layer when the
           * selected Producer's trail is eligible per ADR-016 Open
           * Item 4 (> 50 m within the 15-min window). Mounted after
           * :class:`SnapshotLayer` so the snapshot's source is
           * registered first — the trail layer's ``beforeId``
           * targets the snapshot marker layer so the marker paints
           * on top of the trail.
           *
           * Cross-references:
           *   - REQ-UI-012.
           *   - ADR-016 §5 + Open Items 3, 4, 12.
           */}
          <TrailLayer map={loadedMap} />
          {/* TrackTailLayer — issue #278 follow-up (PR1). A smooth tail
           * behind every sensor showing its recent path, accumulated from
           * the live status SSE stream, trimmed to the operator-configured
           * km (map settings → Platforms → Dynamic Track). Sits below the
           * markers (beforeId) and suppresses the selected sensor's tail
           * (it shows the full selected-trail instead). */}
          <TrackTailLayer map={loadedMap} subscribe={sse.subscribe} />
          {/* SAPIENT geometry washes — PR5b inc3 (FoV) + inc4 (coverage +
           * obscuration); REQ-UI-070; ADR-032 §4.2. Each renders every
           * platform's rings (faint fill + saturated outline) from the shared
           * geometry store, which the status-geometry SSE bridge mounted in
           * :func:`useMapSseBridges` keeps warm. All three are off-by-default
           * + zoom-gated (≥
           * GEOMETRY_MIN_ZOOM) per REQ-UI-070, toggled independently via map
           * settings → Geometry, READ-ONLY on the store (no bridge here — so
           * they take only ``map``), and paint BELOW the markers (beforeId
           * targets the snapshot layer) so the platform markers stay visible
           * over the washes (§8.1). The three layers share one polygon
           * mechanism + one gated-effect hook (CLAUDE.md §4.4); coverage +
           * obscuration are LISTS of rings per platform (one feature per
           * ring), FoV is a single ring.
           *
           * Wash stacking (all below the markers): mounted coverage →
           * obscuration → FoV, so — because each ``beforeId`` insert lands
           * just under the snapshot layer, later inserts sitting above
           * earlier ones — the broad coverage footprint paints at the BOTTOM,
           * obscuration over it, and the sharp FoV sector on TOP of the
           * washes (but still under every marker).
           *
           * Cross-references:
           *   - REQ-UI-070 (SAPIENT map geometry); ADR-032 §4.2; CLAUDE.md §5.8.
           */}
          <CoverageLayer map={loadedMap} />
          <ObscurationLayer map={loadedMap} />
          <FovLayer map={loadedMap} />
          {/* DetectionLayer — PR5b inc 2 (REQ-UI-068; ADR-032 §4.1). The
           * Detection successor to the (now-removed) event glyph layer:
           * renders the shared detections store's rows as cyan glyphs,
           * originates ``detection``-kind selection, consumes selection
           * (ring + 200 ms pan). A null-position detection (unprojectable
           * range/bearing, REQ-INGEST-044) is anchored faint/approximate at
           * its reporting platform's marker when ``platformPositions`` (the
           * snapshot-derived index above) knows it (REQ-UI-071), else
           * list-only. READ-ONLY on the detections store (the SSE bridge is
           * mounted in :func:`useMapSseBridges`; the Detections panel owns the
           * REST feed). Mounted after the geometry washes.
           *
           * Cross-references:
           *   - REQ-UI-068 (Detection map glyphs); REQ-UI-051; ADR-024 §5;
           *     ADR-032 §4.1.
           */}
          {/* ObjectTrackLayer — ADR-041 D-4 (REQ-UI-167 / -168 / -173). A
           * line through each live object track's positioned members, ending
           * at its head glyph, painted in the detection colour and fading by
           * head age over the live window; gated on
           * ``map.visible_layers.tracks`` (hidden ⇒ lines off, heads stay).
           * READ-ONLY on the object-track store (fed by the SSE bridge in
           * :func:`useMapSseBridges`, the viewport backfill above and the
           * Tracks panel's poll). Mounted BEFORE the detection glyphs so the
           * head glyph paints over its own line. */}
          <ObjectTrackLayer map={loadedMap} singleColor={detectionSingleColor} />
          <DetectionLayer
            map={loadedMap}
            singleColor={detectionSingleColor}
            glyph={detectionGlyph}
            platformPositions={platformPositions}
          />
          {/* AlertLayer — PR5b inc 2 (REQ-UI-069; ADR-029; ADR-032 §4.2).
           * Renders the shared alerts store's *active* rows as magenta
           * glyphs ONLY when the alert resolves to a valid location
           * (REQ-UI-069 — else list-only), originates ``alert``-kind
           * selection, consumes selection (ring + 200 ms pan). READ-ONLY on
           * the store (the SSE bridge is mounted in :func:`useMapSseBridges`;
           * the Alerts panel owns the REST feed), so it takes only ``map``.
           * Mounted LAST of the SAPIENT pair so alert glyphs — the highest
           * operator-attention salience (ADR-029 §5) — paint on top of
           * detection glyphs.
           *
           * Cross-references:
           *   - REQ-UI-069 (Alert map glyph); REQ-UI-051; ADR-024 §5;
           *     ADR-029 §6; ADR-032 §4.2.
           */}
          <AlertLayer map={loadedMap} colorConfig={alertColorConfig} glyph={alertGlyph} />
          {/* Annotation geometry — the Meridian-owned render layers (ADR-034
           * design update PR D): fill + per-dash outlines + markers. Mounted
           * before the label layer so labels paint on top. */}
          <AnnotationRenderLayer map={loadedMap} />
          {/* Annotation label / measurement-readout symbol layer (ADR-034 §7 /
           * D12) — this layer owns the text. */}
          <AnnotationLabelLayer map={loadedMap} />
          {/* Edit handles (REQ-UI-135) — mounted last of the annotation layers
           * so the grab points paint ON TOP of the geometry and labels they
           * belong to; a handle hidden under a label is a handle you cannot
           * grab. Present only while the Edit tool is active. */}
          <AnnotationHandleLayer map={loadedMap} />
          {/* Right-click "Copy measurements" menu (#9) — the on-map labels are
           * WebGL (not selectable text), so this is how an operator copies a
           * shape's label + measurement readout to the clipboard. */}
          <AnnotationContextMenu map={loadedMap} />
          {/* CursorReadout — lower-right signed decimal-degree lat/lon of
           * the cursor position (3 decimals). DOM overlay (not a WebGL
           * layer); tracks the map's mousemove and freezes on its last
           * value when the pointer leaves. Mirrors the Flow 03 storyboard
           * ``.coord-readout`` placement; the production format (signed
           * decimal, bare, lat/lon-only) is the in-session §6 decision
           * (2026-05-29) — see that flow's README.
           *
           * Cross-references:
           *   - REQ-UI-002 (MapLibre map surface).
           *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
           */}
          <CursorReadout map={loadedMap} />
          {/* Annotation tool palette (ADR-034 §8.1 / D16) — the drawing toolbar
           * docked top-left; drives the active tool consumed by
           * useAnnotationDraw to switch Terra Draw draw modes. */}
          <AnnotationPalette />
        </>
      )}
      {/* The map-settings cog was removed in the post-inspection pass (item
       * 3a): geometry-layer visibility + dynamic-track tails are now edited in
       * the Profile editor's Map section, so the on-panel cog was redundant. */}
      {/* A11y live region — visually hidden but announced by screen
       * readers when its text content changes. Per Flow 03
       * §"Accessibility" + REQ-UI-059. Lives outside the WebGL
       * canvas so screen readers can reach it. */}
      <div
        ref={clusterSummaryRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="cluster-summary-region"
      />
    </div>
  );
}
